import { useMutation } from "@apollo/client";
import Cancel from "@mui/icons-material/Cancel";
import Person from "@mui/icons-material/Person";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  Typography,
} from "@mui/material";
import { gql } from "generated-graphql";
import { Permission, Role, Tenant as TenantType } from "generated-graphql/graphql";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useAlerts } from "./Alerts/AlertProvider";
import { AssociateFacilitiesDialog } from "./AssociateFacilities/AssociateFacilitiesDialog";
import { Dialog } from "./Dialog";
import { RolePicker } from "./RolePicker";
import { TenantPicker, Tenant as TenantPickerType } from "./TenantPicker";

const SET_EMULATED_ROLE = gql(`
  mutation SetEmulatedRole($tenantId: String!, $permissions: [Permission!]!, $facilityIds: [ID!], $roleId: ID) {
    setEmulatedRole(tenantId: $tenantId, permissions: $permissions, facilityIds: $facilityIds, roleId: $roleId)
  }
`);

const RESET_EMULATED_ROLE = gql(`
  mutation ResetEmulatedRole {
    resetEmulatedRole
  }
`);

const PERMISSIONS_GROUPS: { [group: string]: Permission[] } = {
  Routes: [
    Permission.RouteEpcra,
    Permission.RouteRcra,
    Permission.RouteFacilities,
    Permission.RouteCalendar,
    Permission.RouteContacts,
    Permission.RouteDocuments,
    Permission.RouteEpcraOverview,
    Permission.RouteEpcraTierIi,
    Permission.RouteEpcraReports,
    Permission.RouteEpcraProducts,
    Permission.RouteEpcraCredentials,
    Permission.RouteInsights,
  ],
  Facilities: [
    Permission.ReadAllFacility,
    Permission.ReadAllFacilityMinimal,
    Permission.WriteAllFacility,
    Permission.CreateFacility,
    Permission.EditOwnFacilityPermissions,
    Permission.EditOwnFacilityPermissionsUnlimited,
    Permission.ReadFacilityContact,
    Permission.WriteFacilityContact,
    Permission.VerifyTierIiReport,
    Permission.ManageReviewerRequirements,
    Permission.ViewCredentials,
  ],
  Users: [
    Permission.ReadAllUserTenant,
    Permission.WriteAllUserTenant,
    Permission.CreateUserTenant,
    Permission.ReadUserTenantRole,
    Permission.WriteUserTenantRole,
    Permission.ReadRole,
    Permission.WriteRole,
    Permission.ReadPerson,
    Permission.WritePerson,
  ],
  Tasks: [
    Permission.ReadOrgEventSequences,
    Permission.WriteOrgEventSequences,
    Permission.CreateDataCollectionTask,
    Permission.WriteDataCollectionTask,
    Permission.FulfillDataCollectionTask,
    Permission.WriteAllCalendarEvent,
    // Deprecated
    // Permission.WriteDataCollectionTaskTemplate,
  ],
  Logs: [Permission.ReadSystemAuditLog, Permission.ReadUserActivityLog],
};

type EmulatePermissionsDialogProps = {
  open: boolean;
  tenant?: TenantType | TenantPickerType;
  onClose: () => void;
};

export function EmulatePermissionsDialog({
  open,
  tenant: initialTenant,
  onClose,
}: EmulatePermissionsDialogProps) {
  const { user, refetchUser } = useCurrentUser();
  const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>([]);
  const [selectedFacilityIds, setSelectedFacilityIds] = useState<string[]>([]);
  const alerts = useAlerts();
  const navigate = useNavigate();
  const [setEmulatedRole, { loading: emulateLoading }] = useMutation(SET_EMULATED_ROLE);
  const [resetEmulatedRole, { loading: resetLoading }] = useMutation(RESET_EMULATED_ROLE);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [tenant, setTenant] = useState<TenantType | TenantPickerType | undefined>(initialTenant);
  const [redirectAfterUserUpdate, setRedirectAfterUserUpdate] = useState<string | undefined>(
    undefined
  );
  const [tenantPickerOpen, setTenantPickerOpen] = useState(false);
  const [facilitiesDialogOpen, setFacilitiesDialogOpen] = useState(false);

  useEffect(() => {
    if (redirectAfterUserUpdate) {
      navigate(redirectAfterUserUpdate);
      setRedirectAfterUserUpdate(undefined);
    }
    // oxlint-disable-next-line exhaustive-deps -- Grandfathered in error. Future hooks should be exhaustive
  }, [user]);

  useEffect(() => {
    if (selectedRole) {
      const rolePermissions = selectedRole.permissions as Permission[];
      setSelectedPermissions(rolePermissions);

      // Clear facility selections if role has access to all facilities
      if (
        rolePermissions.includes(Permission.ReadAllFacility) ||
        rolePermissions.includes(Permission.WriteAllFacility)
      ) {
        setSelectedFacilityIds([]);
      }
    }
  }, [selectedRole, user?.isEmulating]);

  useEffect(() => {
    if (initialTenant) {
      setTenant(initialTenant);
    }
  }, [initialTenant]);

  const handleEmulate = async () => {
    try {
      if (!tenant) {
        alerts.error("Please select a tenant");
        return;
      }
      await setEmulatedRole({
        variables: {
          tenantId: tenant?.id ?? "",
          permissions: selectedPermissions,
          facilityIds: selectedFacilityIds,
          roleId: selectedRole?.id,
        },
      });
      alerts.success("Successfully emulated user");
      setRedirectAfterUserUpdate(`/`);
      refetchUser();
      onClose();
    } catch (error) {
      alerts.error("Failed to emulate user");
    }
  };

  const handleResetEmulation = async () => {
    try {
      await resetEmulatedRole();
      alerts.success("Successfully reset emulation");
      if (tenant) {
        setRedirectAfterUserUpdate(`/o/${tenant.id}/facilities`);
      }
      refetchUser();
      onClose();
    } catch (error) {
      alerts.error("Failed to reset emulation");
    }
  };

  const handlePermissionsChange = (permission: Permission) => {
    setSelectedPermissions((prev) => {
      const newPermissions = prev.includes(permission)
        ? prev.filter((p) => p !== permission)
        : [...prev, permission];

      // Clear facility selections if user now has access to all facilities
      if (
        (permission === Permission.ReadAllFacility || permission === Permission.WriteAllFacility) &&
        newPermissions.includes(permission)
      ) {
        setSelectedFacilityIds([]);
      }

      return newPermissions;
    });
  };

  const handleGroupClick = (group: string) => {
    const permissions = PERMISSIONS_GROUPS[group];
    const allSelected = permissions.every((permission) => selectedPermissions.includes(permission));

    setSelectedPermissions((prev) => {
      const newPermissions = allSelected
        ? prev.filter((p) => !permissions.includes(p))
        : [...prev, ...permissions.filter((p) => !prev.includes(p))];

      // Clear facility selections if user now has access to all facilities
      if (
        !allSelected && // Only when adding permissions
        (permissions.includes(Permission.ReadAllFacility) ||
          permissions.includes(Permission.WriteAllFacility))
      ) {
        setSelectedFacilityIds([]);
      }

      return newPermissions;
    });
  };

  const handleTenantSelected = (selectedTenant: TenantPickerType) => {
    setTenant(selectedTenant);
    setTenantPickerOpen(false);
  };

  const handleOnClose = () => {
    setTenantPickerOpen(false);
    setTenant(initialTenant);
    setSelectedRole(null);
    setSelectedPermissions([]);
    setSelectedFacilityIds([]);
    setFacilitiesDialogOpen(false);
    onClose();
  };

  if (user?.isEmulating)
    return (
      <Dialog
        open={open}
        onClose={handleOnClose}
        maxWidth="lg"
        title="Emulate Permissions"
        primaryActionButtonProps={{
          loading: emulateLoading || resetLoading,
          onClick: handleResetEmulation,
          startIcon: <Cancel />,
          disabled: !tenant,
          saveText: "Reset",
        }}
      >
        <Typography>
          You are currently emulating a role in {tenant?.name}. Please reset to emulate a different
          role or to go back to being a staff user.
        </Typography>
      </Dialog>
    );

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      maxWidth="lg"
      title="Emulate Permissions"
      primaryActionButtonProps={{
        loading: emulateLoading || resetLoading,
        onClick: handleEmulate,
        startIcon: <Person />,
        disabled: !tenant || selectedPermissions.length === 0,
        saveText: "Emulate",
      }}
      description="Select a tenant and role to view that tenant as a specific user role, or
      select a combination of permissions to see what a user with those
      permissions would see."
    >
      {!!tenant && (
        <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
          <Typography>Current Tenant: {tenant.name}</Typography>
          <Button onClick={() => setTenantPickerOpen(!tenantPickerOpen)}>Change</Button>
        </Box>
      )}
      {(tenantPickerOpen || !tenant) && <TenantPicker onTenantSelected={handleTenantSelected} />}
      {!!tenant && (
        <>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <RolePicker
              tenantId={tenant?.id ?? ""}
              value={selectedRole?.id ?? ""}
              onChange={setSelectedRole}
            />
          </FormControl>

          {/* Facility Selection for Field + Facility Users */}
          {selectedPermissions.includes(Permission.FulfillDataCollectionTask) &&
            !selectedPermissions.includes(Permission.ReadAllFacility) &&
            !selectedPermissions.includes(Permission.WriteAllFacility) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Facility Access
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select facilities to grant access to. Leave empty to test users with no facility
                  access.
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Button variant="outlined" onClick={() => setFacilitiesDialogOpen(true)}>
                    Select Facilities ({selectedFacilityIds.length})
                  </Button>
                  {selectedFacilityIds.length > 0 && (
                    <Button size="small" color="error" onClick={() => setSelectedFacilityIds([])}>
                      Clear All
                    </Button>
                  )}
                </Box>
              </Box>
            )}

          {/* Show message when facility picker is hidden due to all-facility permissions */}
          {selectedPermissions.includes(Permission.FulfillDataCollectionTask) &&
            (selectedPermissions.includes(Permission.ReadAllFacility) ||
              selectedPermissions.includes(Permission.WriteAllFacility)) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                  ℹ️ Facility selection is not needed - this role has access to all facilities in
                  the tenant.
                </Typography>
              </Box>
            )}

          <Grid container spacing={2} sx={{ mt: 2 }}>
            {Object.entries(PERMISSIONS_GROUPS).map(([group, permissions]) => (
              <Grid item xs={6} key={group}>
                <FormControl component="fieldset" variant="standard">
                  <Box
                    fontWeight="fontWeightBold"
                    mb={1}
                    onClick={() => handleGroupClick(group)}
                    sx={{ cursor: "pointer" }}
                  >
                    {group}
                  </Box>
                  {permissions.map((permission) => (
                    <FormControlLabel
                      key={permission}
                      control={
                        <Checkbox
                          checked={selectedPermissions.includes(permission)}
                          onChange={() => handlePermissionsChange(permission)}
                        />
                      }
                      label={permission}
                    />
                  ))}
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Associate Facilities Dialog */}
      <AssociateFacilitiesDialog
        open={facilitiesDialogOpen}
        onClose={() => setFacilitiesDialogOpen(false)}
        existingFacilityIds={selectedFacilityIds}
        onSave={(facilityIds) => {
          setSelectedFacilityIds(facilityIds);
          setFacilitiesDialogOpen(false);
        }}
        subtitle="Select facilities to grant access to for this emulated role."
        allFacilities={false}
        showTenantColumn={false}
      />
    </Dialog>
  );
}
